import { useMemo, useState } from 'react';
import { X } from 'lucide-react';
import type { Connector } from 'wagmi';
import { useConnect } from 'wagmi';
import { closeWalletModal, useWalletModalOpen } from '../web3/walletModalStore';
import { pickConnectors } from '../web3/useWallet';

// friendly labels + emoji icons for the common connectors (no external assets)
function meta(connector: Connector): { label: string; icon: string; sub: string } {
  const id = connector.id.toLowerCase();
  const name = connector.name;
  if (id.includes('walletconnect')) return { label: 'WalletConnect', icon: '🔗', sub: '모바일 지갑 앱 (QR·딥링크)' };
  if (id.includes('coinbase')) return { label: 'Coinbase Wallet', icon: '🟦', sub: '코인베이스 지갑' };
  if (name.toLowerCase().includes('metamask')) return { label: 'MetaMask', icon: '🦊', sub: '브라우저 확장 · 인앱' };
  if (id === 'injected') return { label: name || '브라우저 지갑', icon: '🌐', sub: '설치된 브라우저 지갑' };
  return { label: name, icon: '👛', sub: '설치된 지갑' };
}

export default function WalletModal() {
  const open = useWalletModalOpen();
  const { connectors: allConnectors, connect, isPending } = useConnect();
  const [error, setError] = useState<string | null>(null);
  const connectors = useMemo(() => pickConnectors(allConnectors), [allConnectors]);

  const connectWith = (connector: Connector) => {
    setError(null);
    connect(
      { connector },
      {
        onSuccess: () => closeWalletModal(),
        onError: (err) => setError(err.message),
      },
    );
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={closeWalletModal} />
      <div className="relative w-full max-w-sm rounded-2xl border border-abyss-700 bg-abyss-900 shadow-2xl shadow-black/50 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-abyss-700">
          <div>
            <h3 className="text-base font-semibold text-foam-100">지갑 연결</h3>
            <p className="text-[11px] text-foam-500 mt-0.5">연결할 지갑을 선택하세요</p>
          </div>
          <button
            onClick={closeWalletModal}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-foam-400 hover:bg-abyss-700/60 transition-colors"
            aria-label="닫기"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-3 space-y-2">
          {connectors.length === 0 && (
            <div className="text-xs text-foam-500 py-4 text-center">사용 가능한 지갑이 없습니다.</div>
          )}
          {connectors.map((connector) => {
            const m = meta(connector);
            return (
              <button
                key={connector.uid}
                onClick={() => connectWith(connector)}
                disabled={isPending}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-abyss-700 bg-abyss-850 hover:border-tide-400/50 hover:bg-abyss-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed text-left"
              >
                <span className="text-2xl leading-none w-8 text-center shrink-0">{m.icon}</span>
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-foam-100">{m.label}</span>
                  <span className="block text-[11px] text-foam-500">{m.sub}</span>
                </span>
              </button>
            );
          })}
        </div>

        {error && (
          <div className="mx-3 mb-3 px-3 py-2 rounded-md bg-coral-500/15 text-coral-400 text-xs break-words">
            {error}
          </div>
        )}

        <div className="px-5 py-3 border-t border-abyss-700 text-[11px] text-foam-500 leading-relaxed">
          모바일에서는 <span className="text-foam-300">WalletConnect</span>를 선택하면 설치된 지갑 앱으로
          연결됩니다. 논커스터디얼 — 자금은 항상 회원 지갑에 있습니다.
        </div>
      </div>
    </div>
  );
}
